import { useMemo } from 'react';

import { getTiebreakerSize } from '@/components/scoreboard/TiebreakerScore';
import {
    TENNIS_PERIOD_SCORE_TYPE,
    formatTennisGameScore,
    getServingSide,
    getTieBreakPoints,
} from '@/components/scoreboard/sports/tennis';
import { EventState } from '@/components/scoreboard/types';
import { getEventState } from '@/components/scoreboard/use-scoreboard';
import { Event, PeriodScore, SportSpecific } from '@/feature/event-details-sbk/types';
import { useEventDetailsCache } from '@/hooks/use-sbk-event-details-cache';
import { useShallow } from 'zustand/react/shallow';

import { TennisSetScore } from './TennisScoreboard';

type TennisScoreboard = {
    setScores: TennisSetScore[];
    gameScore?: string;
    isServing: boolean;
};

type TennisScoreboards = {
    home: TennisScoreboard;
    away: TennisScoreboard;
};

export const buildTennisScoreboard = (
    periodScores: PeriodScore[] | undefined,
    sportSpecific: SportSpecific | undefined,
    eventState: EventState,
    side: 'home' | 'away'
): TennisScoreboard => {
    const tennisPeriodScores = (periodScores ?? []).filter(p => p.type === TENNIS_PERIOD_SCORE_TYPE);
    const isLive = eventState === 'live';
    const isFinished = eventState === 'final';
    // When live, the API keeps the in-progress set as the last entry. Mark it active so the
    // scoreboard can highlight it. isWinner is only meaningful on completed matches.
    const activeIndex = !isFinished && tennisPeriodScores.length > 0 ? tennisPeriodScores.length - 1 : -1;
    const opponentSide = side === 'home' ? 'away' : 'home';

    const setScores: TennisSetScore[] = tennisPeriodScores.map((p, index) => {
        const mine = side === 'home' ? p.home_score : p.away_score;
        const opponent = side === 'home' ? p.away_score : p.home_score;
        const tieBreak = getTieBreakPoints(sportSpecific?.points_won_per_game, p, side);
        const opponentTieBreak = getTieBreakPoints(sportSpecific?.points_won_per_game, p, opponentSide);
        return {
            score: mine,
            tieBreak,
            tieBreakSize: getTiebreakerSize(tieBreak, opponentTieBreak),
            isActive: index === activeIndex,
            isWinner: isFinished && mine > opponent,
        };
    });

    const gameScore = sportSpecific?.current_game_score?.[side];

    return {
        setScores,
        gameScore:
            isLive && gameScore !== undefined
                ? formatTennisGameScore(gameScore, sportSpecific?.is_match_in_tie_break)
                : undefined,
        isServing: getServingSide(sportSpecific, isLive) === side,
    };
};

// Live-updating tennis scoreboard, driven by the event-details cache with a fallback to the
// initial Event data. Game score and serving indicator only render live; winner highlighting
// only applies when the match is Final.
export const useTennisScoreboards = (event: Event): TennisScoreboards => {
    const { periodScores, sportSpecific, eventState } = useEventDetailsCache(
        useShallow(state => {
            const cached = state.eventDetails[event.id];
            return {
                periodScores: cached?.period_scores ?? event.event_details?.period_scores,
                sportSpecific: cached?.sport_specific ?? event.event_details?.sport_specific,
                eventState: getEventState(cached?.status ?? event.status),
            };
        })
    );

    return useMemo(
        () => ({
            home: buildTennisScoreboard(periodScores, sportSpecific, eventState, 'home'),
            away: buildTennisScoreboard(periodScores, sportSpecific, eventState, 'away'),
        }),
        [periodScores, sportSpecific, eventState]
    );
};
